import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { ThemeModeContext } from '@luigiclaudio/ga-baseline-ui/theme-provider';
import { useHasMounted } from '@luigiclaudio/ga-baseline-ui/helpers';
import ColorVariantOuterContainer from './ColorVariantOuterContainer';
import ColorVariantContainer from './ColorVariantContainer';
import ContainerHeight from './ContainerHeight';
import colorToolsUtils from '../../utils/colorToolsUtils';
import useCopied from '../../utils/useCopied';
import ColorVariantFooter from './ColorVariantFooter';
import useAllColorUserCache from '../../services/useAllColorUserCache';
import ColorVarianStepBar from './ColorVarianStepBar';
import ColorVariantWrapper from './ColorVariantWrapper';
import CustomLabelContainer from './CustomLabelContainer';

const ColorVariant = ({
    colorVariant,
    stepValue,
    showLabel,
    showIsFavorite,
    customLabel,
    minHeight,
    widthValue,
    allowCopyToClipboard,
    useCustomLabelHeight,
    stepCount,
    stepLabel,
}) => {
    const ref = useRef();
    const { themeMode } = useContext(ThemeModeContext);
    const { allColorsUserFromClient } = useAllColorUserCache();
    const hasMounted = useHasMounted();

    const [currentColor, setCurrentColor] = useState(undefined);
    const [hasChanged, setHasChanged] = useState(false);

    const [customLabelHeight, setCustomLabelHeight] = useState();

    const checkCustomLabelHeight = () => {
        if (ref.current) {
            setCustomLabelHeight(ref.current?.getBoundingClientRect().height);
        }
    };

    const { copyToClipboard } = useCopied();

    useEffect(() => {
        setHasChanged(true);
        const timer = setTimeout(() => {
            setHasChanged(false);
        }, 150);
        return () => clearTimeout(timer);
    }, [stepValue]);

    useEffect(() => {
        if (colorToolsUtils.isValidColor(colorVariant?.slice(1))) {
            setCurrentColor(colorVariant);
        } else {
            setCurrentColor(undefined);
        }
    }, [colorVariant, hasChanged]);

    useEffect(() => {
        checkCustomLabelHeight();
        window.addEventListener('resize', checkCustomLabelHeight);
        return () => window.removeEventListener('resize', checkCustomLabelHeight);
    }, [hasMounted, customLabel]);

    if (!hasMounted) {
        return null;
    }

    const isFavorite = allColorsUserFromClient?.allColors?.data?.find(
        (item) => item.color === colorVariant,
    );

    const renderMinHeight =
        customLabel && !useCustomLabelHeight ? customLabelHeight + minHeight : minHeight;

    return (
        <ColorVariantWrapper widthValue={widthValue}>
            <ColorVarianStepBar
                value={stepCount}
                stepValue={stepCount}
                stepLabel={stepLabel}
                hasChanged={hasChanged}
            />
            <ContainerHeight widthValue={widthValue}>
                <ColorVariantOuterContainer
                    minHeight={renderMinHeight}
                    stepValue={stepValue}
                    colorVariant={currentColor}
                    hasChanged={hasChanged}
                >
                    <ColorVariantContainer
                        style={{ backgroundColor: colorVariant }}
                        colorVariant={currentColor}
                        onClick={() => {
                            if (currentColor && allowCopyToClipboard) {
                                copyToClipboard({
                                    valueCopied: currentColor.slice(1),
                                    labelCopied: currentColor,
                                });
                            }
                        }}
                        showLabel={showLabel}
                        themeMode={themeMode}
                        allowCopyToClipboard={allowCopyToClipboard}
                        isFavorite={!!isFavorite?.id}
                        showIsFavorite={showIsFavorite}
                    />
                    <ColorVariantFooter
                        colorValue={currentColor}
                        show={showLabel && !customLabel}
                        showAddToFavorites={showLabel}
                    />
                    {customLabel && (
                        <CustomLabelContainer ref={ref}>{customLabel}</CustomLabelContainer>
                    )}
                </ColorVariantOuterContainer>
            </ContainerHeight>
        </ColorVariantWrapper>
    );
};

ColorVariant.propTypes = {
    colorVariant: PropTypes.string,
    stepValue: PropTypes.number,
    showLabel: PropTypes.bool,
    showIsFavorite: PropTypes.bool,
    minHeight: PropTypes.number,
    customLabel: PropTypes.node,
    widthValue: PropTypes.number,
    allowCopyToClipboard: PropTypes.bool,
    useCustomLabelHeight: PropTypes.bool,
    stepCount: PropTypes.number,
    stepLabel: PropTypes.string,
};

ColorVariant.defaultProps = {
    colorVariant: undefined,
    stepValue: undefined,
    showLabel: true,
    showIsFavorite: true,
    minHeight: 96,
    customLabel: undefined,
    widthValue: 1,
    allowCopyToClipboard: true,
    useCustomLabelHeight: false,
    stepCount: undefined,
    stepLabel: undefined,
};

export default ColorVariant;
